import {
  isReservedProductBarcode,
  parseBahtToSatang,
  ScannerBurstNormalizer,
} from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { PageHeader } from '../../components/PageHeader';
import { listCategories, listProducts } from '../../data/fetchers';
import { upsertProduct } from '../../data/mutators';
import { fmtAmount, fmtInt } from '../../lib/format';
import { canEditMasterData } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { Product, ProductUnit } from '../../types/domain';
import {
  absoluteToRelativeFactors,
  baseCostFromSelectedUnit,
  classifyPriceHealth,
  derivedUnitCost,
  type PriceHealthStatus,
  relativeToAbsoluteFactors,
} from './productUnitPricing';

type ProductFields = Omit<Product, 'baseUnit' | 'baseUnitId' | 'units'>;

interface UnitDraft extends ProductUnit {
  id: string;
  relativeInput: string;
  priceInput: string;
}

let unitDraftSequence = 0;

const newUnitId = (): string => {
  const generated = globalThis.crypto?.randomUUID?.();
  if (generated) return `product-unit:${generated}`;
  unitDraftSequence += 1;
  return `product-unit:${Date.now()}:${unitDraftSequence}`;
};

const createUnitDraft = (
  unit: ProductUnit = { unit: 'ชิ้น', factor: 1, price: 0, barcode: '' },
  relativeInput = '1',
): UnitDraft => ({
  ...unit,
  id: unit.id ?? newUnitId(),
  relativeInput,
  priceInput: moneyInputValue(unit.price),
});

const blank = (): ProductFields => ({
  id: '',
  name: '',
  categoryId: '',
  active: true,
  minStock: 0,
  imageUrl: '',
  cost: 0,
});

const THAI_NUMBER_ROW: Record<string, string> = {
  ๅ: '1',
  ภ: '4',
  ถ: '5',
  'ุ': '6',
  'ึ': '7',
  ค: '8',
  ต: '9',
  จ: '0',
};
const THAI_NUMBER_ROW_SIGNALS = new Set([
  'ๅ',
  'ภ',
  'ถ',
  'ุ',
  'ึ',
  'ค',
  'ต',
  'จ',
]);

export function normalizeBarcodeKeyboardValue(value: string): string {
  const characters = [...value];
  const hasUnambiguousThaiKey = characters.some((char) =>
    THAI_NUMBER_ROW_SIGNALS.has(char),
  );
  if (!hasUnambiguousThaiKey) {
    return value;
  }
  return characters
    .map((char) =>
      THAI_NUMBER_ROW_SIGNALS.has(char) ? THAI_NUMBER_ROW[char] : char,
    )
    .join('');
}

function moneyInputValue(satang: number): string {
  if (!satang) return '';
  return String(satang / 100);
}

function FieldLabel({ children }: { children: ReactNode }) {
  return <span className="text-sm-label font-bold text-ink-2">{children}</span>;
}

function BarcodeInput({
  index,
  value,
  disabled,
  error,
  onChange,
}: {
  index: number;
  value: string;
  disabled: boolean;
  error?: string;
  onChange: (value: string) => void;
}) {
  const scannerBurst = useRef(new ScannerBurstNormalizer());

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const normalizedScan = scannerBurst.current.complete(event.nativeEvent);
      if (normalizedScan) onChange(normalizedScan);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    scannerBurst.current.push(event.nativeEvent);
  };

  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <FieldLabel>Barcode (ไม่บังคับ)</FieldLabel>
      <input
        aria-label={`บาร์โค้ดแถว ${index + 1}`}
        disabled={disabled}
        type="text"
        value={value}
        onChange={(event) =>
          onChange(normalizeBarcodeKeyboardValue(event.target.value))
        }
        onKeyDown={handleKeyDown}
        className={`h-10 w-full rounded-btn border bg-surface px-3 font-mono text-body focus:outline-none focus:ring-2 disabled:bg-bg-warm ${
          error
            ? 'border-danger focus:border-danger focus:ring-danger/10'
            : 'border-line focus:border-primary focus:ring-primary/10'
        }`}
        placeholder="สแกนหรือกรอก Barcode"
      />
      {error ? (
        <span role="alert" className="text-xs font-semibold text-danger">
          {error}
        </span>
      ) : value ? (
        <span className="text-xs text-ink-3">
          ขายได้ทั้งสแกนและเลือกที่ POS
        </span>
      ) : (
        <span className="text-xs text-ink-3">
          ไม่มี Barcode — ขายผ่านการค้นหาชื่อหรือ Product Picker
        </span>
      )}
    </label>
  );
}

const healthStyle: Record<
  PriceHealthStatus,
  { label: string; className: string }
> = {
  unknown: { label: 'รอข้อมูล', className: 'bg-bg-warm text-ink-3' },
  loss: { label: 'ขาดทุน', className: 'bg-danger-light text-danger' },
  'low-margin': { label: 'กำไรต่ำ', className: 'bg-warn-light text-warn' },
  healthy: { label: 'ปกติ', className: 'bg-ok-light text-ok' },
};

export function ProductFormView() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useSession((state) => state.user);
  const canEdit = user ? canEditMasterData(user) : false;

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });

  const [form, setForm] = useState<ProductFields>(blank());
  const [unitDrafts, setUnitDrafts] = useState<UnitDraft[]>(() => [
    createUnitDraft(),
  ]);
  const [minStockInput, setMinStockInput] = useState('');
  const [costUnitId, setCostUnitId] = useState(() => unitDrafts[0]!.id);
  const [costInput, setCostInput] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!isNew) return;
    const baseUnit = createUnitDraft();
    setForm(blank());
    setUnitDrafts([baseUnit]);
    setMinStockInput('');
    setCostUnitId(baseUnit.id);
    setCostInput('');
    setSubmitError('');
  }, [id, isNew]);

  useEffect(() => {
    if (!isNew || categories.length === 0) return;
    setForm((current) =>
      categories.some((category) => category.id === current.categoryId)
        ? current
        : { ...current, categoryId: categories[0]!.id },
    );
  }, [categories, isNew]);

  useEffect(() => {
    if (isNew) return;
    const found = products.find((product) => product.id === id);
    if (!found) return;
    const sortedUnits = [...found.units].sort(
      (left, right) => left.factor - right.factor,
    );
    const relativeFactors = absoluteToRelativeFactors(
      sortedUnits.map((unit) => unit.factor),
    );
    const nextUnitDrafts = sortedUnits.map((unit, index) =>
      createUnitDraft(unit, String(relativeFactors[index] ?? 1)),
    );
    const nextCostUnit = nextUnitDrafts.at(-1) ?? createUnitDraft();
    setForm({
      id: found.id,
      name: found.name,
      categoryId: found.categoryId,
      active: found.active,
      minStock: found.minStock,
      imageUrl: found.imageUrl,
      cost: found.cost,
    });
    setUnitDrafts(nextUnitDrafts.length > 0 ? nextUnitDrafts : [nextCostUnit]);
    setMinStockInput(String(found.minStock ?? 0));
    setCostUnitId(nextCostUnit.id);
    setCostInput(moneyInputValue((found.cost ?? 0) * nextCostUnit.factor));
    setSubmitError('');
  }, [id, isNew, products]);

  const mutation = useMutation({
    mutationFn: upsertProduct,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['products-page'] });
      navigate('/products');
    },
  });

  const set = <K extends keyof ProductFields>(
    key: K,
    value: ProductFields[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  const setUnit = (unitId: string, patch: Partial<UnitDraft>) =>
    setUnitDrafts((current) =>
      current.map((unit) =>
        unit.id === unitId ? { ...unit, ...patch } : unit,
      ),
    );

  const syncUnitDrafts = (
    nextDrafts: UnitDraft[],
    selectedCostUnitId = costUnitId,
    selectedCostInput = costInput,
  ) => {
    const absoluteFactors = relativeToAbsoluteFactors(
      nextDrafts.map((unit, index) =>
        index === 0 ? 1 : Number(unit.relativeInput),
      ),
    );
    const units = nextDrafts.map((unit, index) => ({
      ...unit,
      relativeInput: index === 0 ? '1' : unit.relativeInput,
      factor: absoluteFactors[index] ?? Number.NaN,
    }));
    const selectedFactor =
      units.find((unit) => unit.id === selectedCostUnitId)?.factor ?? 1;
    const selectedCost = selectedCostInput
      ? parseBahtToSatang(selectedCostInput)
      : 0;
    setUnitDrafts(units);
    setForm((current) => ({
      ...current,
      cost: baseCostFromSelectedUnit(selectedCost, selectedFactor),
    }));
  };

  const addUnit = () => {
    const previousLargest = unitDrafts.at(-1)!;
    const nextUnit = createUnitDraft(
      { unit: '', factor: previousLargest.factor, price: 0, barcode: '' },
      '1',
    );
    const followLargest = costUnitId === previousLargest.id;
    const nextCostUnitId = followLargest ? nextUnit.id : costUnitId;
    syncUnitDrafts([...unitDrafts, nextUnit], nextCostUnitId);
    if (followLargest) {
      setCostUnitId(nextUnit.id);
      setCostInput(moneyInputValue((form.cost ?? 0) * nextUnit.factor));
    }
  };

  const removeUnit = (unitId: string) => {
    const index = unitDrafts.findIndex((unit) => unit.id === unitId);
    if (index === 0) return;
    const nextUnits = unitDrafts.filter((unit) => unit.id !== unitId);
    const nextCostUnitId =
      costUnitId === unitId ? nextUnits.at(-1)!.id : costUnitId;
    if (costUnitId === unitId) {
      const absoluteFactors = relativeToAbsoluteFactors(
        nextUnits.map((unit, unitIndex) =>
          unitIndex === 0 ? 1 : Number(unit.relativeInput),
        ),
      );
      const nextCostIndex = nextUnits.findIndex(
        (unit) => unit.id === nextCostUnitId,
      );
      const nextCostInput = moneyInputValue(
        (form.cost ?? 0) * (absoluteFactors[nextCostIndex] ?? 1),
      );
      setCostInput(nextCostInput);
      syncUnitDrafts(nextUnits, nextCostUnitId, nextCostInput);
    } else {
      syncUnitDrafts(nextUnits, nextCostUnitId);
    }
    setCostUnitId(nextCostUnitId);
  };

  const barcodeErrors = useMemo(() => {
    const errors = new Map<number, string>();
    const seen = new Map<string, number>();
    unitDrafts.forEach((unit, index) => {
      const barcode = unit.barcode?.trim();
      if (!barcode) return;
      if (isReservedProductBarcode(barcode)) {
        errors.set(
          index,
          'Barcode ขึ้นต้นด้วย QTY ไม่ได้ เนื่องจากสงวนไว้สำหรับคำสั่งจำนวนของ POS',
        );
        return;
      }
      const firstIndex = seen.get(barcode);
      if (firstIndex !== undefined) {
        errors.set(index, `Barcode ซ้ำกับหน่วยแถว ${firstIndex + 1}`);
        errors.set(firstIndex, `Barcode ซ้ำกับหน่วยแถว ${index + 1}`);
      } else {
        seen.set(barcode, index);
      }
      for (const product of products) {
        if (product.id === form.id) continue;
        const ownerUnit = product.units.find(
          (candidate) => candidate.barcode?.trim() === barcode,
        );
        if (ownerUnit) {
          errors.set(
            index,
            `ถูกใช้แล้วกับ ${product.name} (${ownerUnit.unit})`,
          );
          break;
        }
      }
    });
    return errors;
  }, [form.id, products, unitDrafts]);

  const unitHealth = unitDrafts.map((unit) => {
    const cost = derivedUnitCost(form.cost ?? 0, unit.factor);
    return { cost, ...classifyPriceHealth(unit.price, cost) };
  });
  const lossCount = unitHealth.filter((item) => item.status === 'loss').length;
  const warningCount = unitHealth.filter(
    (item) => item.status === 'low-margin',
  ).length;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    setSubmitError('');
    if (barcodeErrors.size > 0) {
      setSubmitError('กรุณาแก้ไข Barcode ที่ไม่ถูกต้องก่อนบันทึก');
      return;
    }
    if (
      unitDrafts.some(
        (unit) => !Number.isFinite(unit.factor) || unit.factor <= 0,
      )
    ) {
      setSubmitError('กรุณากรอกจำนวนต่อหน่วยให้ถูกต้อง');
      return;
    }
    if (
      lossCount > 0 &&
      !window.confirm(
        `มีราคาขายต่ำกว่าทุน ${lossCount} หน่วย ยืนยันบันทึกราคาขาดทุนหรือไม่?`,
      )
    ) {
      return;
    }
    const baseUnit = unitDrafts[0]!;
    const units = unitDrafts.map(
      ({ relativeInput: _relativeInput, priceInput: _priceInput, ...unit }) =>
        unit,
    );
    mutation.mutate({
      ...form,
      id: form.id || `p${Date.now()}`,
      baseUnit: baseUnit.unit,
      baseUnitId: baseUnit.id,
      units,
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => set('imageUrl', reader.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <div className="bo-fade mx-auto w-full max-w-6xl">
      <PageHeader
        title={isNew ? 'เพิ่มสินค้า' : 'แก้ไขสินค้า'}
        subtitle={
          canEdit
            ? 'กำหนดหน่วย ต้นทุน และราคาขายจากของที่ใช้จริงในร้าน'
            : 'อ่านอย่างเดียว (ไม่มีสิทธิ์แก้ไข)'
        }
      />

      <form
        onSubmit={onSubmit}
        className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3"
      >
        <div className="space-y-4 lg:col-span-2">
          <Card title="1. ข้อมูลสินค้า">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5 sm:col-span-2">
                <FieldLabel>ชื่อสินค้า</FieldLabel>
                <input
                  aria-label="ชื่อสินค้า"
                  disabled={!canEdit}
                  value={form.name}
                  onChange={(event) => set('name', event.target.value)}
                  className="h-10 rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:bg-bg-warm"
                  placeholder="เช่น กล้วยหอม, ขนม Homemade"
                  required
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <FieldLabel>หมวดหมู่</FieldLabel>
                <select
                  aria-label="หมวดหมู่"
                  disabled={!canEdit || categories.length === 0}
                  value={form.categoryId}
                  onChange={(event) => set('categoryId', event.target.value)}
                  required
                  className="h-10 rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:bg-bg-warm"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <FieldLabel>สต๊อกขั้นต่ำ</FieldLabel>
                <input
                  aria-label="สต๊อกขั้นต่ำ"
                  disabled={!canEdit}
                  type="number"
                  min={0}
                  value={minStockInput}
                  onChange={(event) => {
                    setMinStockInput(event.target.value);
                    set(
                      'minStock',
                      event.target.value === ''
                        ? 0
                        : Number(event.target.value),
                    );
                  }}
                  className="num h-10 rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:bg-bg-warm"
                  placeholder="0"
                />
              </label>

              <label className="flex items-center gap-2.5 sm:col-span-2">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={form.active}
                  onChange={(event) => set('active', event.target.checked)}
                  className="h-4.5 w-4.5 accent-primary"
                />
                <span className="text-body font-semibold text-ink-2">
                  เปิดใช้งานขายสินค้านี้
                </span>
              </label>
            </div>
          </Card>

          <Card
            title="2. หน่วยสินค้า"
            actions={
              canEdit ? (
                <Btn kind="default" size="sm" type="button" onClick={addUnit}>
                  + เพิ่มหน่วย
                </Btn>
              ) : undefined
            }
          >
            <div className="mb-4 rounded-btn bg-primary-light px-4 py-3 text-sm font-semibold text-primary">
              เริ่มจากหน่วยเล็กที่สุด แล้วบอกว่าหน่วยถัดไปมีกี่หน่วยก่อนหน้า
              ระบบจะคำนวณจำนวนรวมให้เอง
            </div>

            <div className="space-y-3">
              {unitDrafts.map((unit, index) => {
                const previousUnit = unitDrafts[index - 1];
                return (
                  <div
                    key={unit.id}
                    className="rounded-card border border-line-2 bg-bg/30 p-4"
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="font-bold text-ink">
                          {index === 0
                            ? 'หน่วยเล็กที่สุด'
                            : `หน่วยลำดับที่ ${index + 1}`}
                        </div>
                        <div className="text-xs text-ink-3">
                          {index === 0
                            ? 'สต๊อกและต้นทุนหลักจะอ้างอิงหน่วยนี้'
                            : `รวม ${Number.isFinite(unit.factor) ? fmtInt(unit.factor) : '—'} ${unitDrafts[0]?.unit || 'หน่วยฐาน'}`}
                        </div>
                      </div>
                      {canEdit && index > 0 && (
                        <button
                          type="button"
                          onClick={() => removeUnit(unit.id)}
                          className="rounded-btn px-3 py-2 text-xs font-bold text-danger hover:bg-danger/5"
                        >
                          ลบหน่วย
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="flex flex-col gap-1.5">
                        <FieldLabel>ชื่อหน่วย</FieldLabel>
                        <input
                          aria-label={`หน่วยแถว ${index + 1}`}
                          disabled={!canEdit}
                          value={unit.unit}
                          onChange={(event) =>
                            setUnit(unit.id, { unit: event.target.value })
                          }
                          className="h-10 rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10 disabled:bg-bg-warm"
                          placeholder={
                            index === 0 ? 'เช่น ชิ้น' : 'เช่น แพ็ค, ลัง'
                          }
                          required
                        />
                      </label>

                      {index > 0 ? (
                        <label className="flex flex-col gap-1.5">
                          <FieldLabel>
                            1 {unit.unit || 'หน่วยนี้'} มีกี่{' '}
                            {previousUnit?.unit || 'หน่วยก่อนหน้า'}
                          </FieldLabel>
                          <input
                            aria-label={`จำนวนต่อหน่วยแถว ${index + 1}`}
                            disabled={!canEdit}
                            type="number"
                            min="0.001"
                            step="any"
                            value={unit.relativeInput}
                            onChange={(event) => {
                              syncUnitDrafts(
                                unitDrafts.map((candidate) =>
                                  candidate.id === unit.id
                                    ? {
                                        ...candidate,
                                        relativeInput: event.target.value,
                                      }
                                    : candidate,
                                ),
                              );
                            }}
                            className="num h-10 rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10 disabled:bg-bg-warm"
                            required
                          />
                        </label>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          <FieldLabel>จำนวนเทียบหน่วยฐาน</FieldLabel>
                          <div className="flex h-10 items-center rounded-btn border border-line-2 bg-bg-warm px-3 font-bold text-ink-3">
                            1
                          </div>
                        </div>
                      )}

                      <BarcodeInput
                        index={index}
                        value={unit.barcode ?? ''}
                        disabled={!canEdit}
                        error={barcodeErrors.get(index)}
                        onChange={(barcode) => setUnit(unit.id, { barcode })}
                      />

                      <div className="flex flex-col gap-1.5">
                        <FieldLabel>จำนวนรวมในหน่วยเล็กสุด</FieldLabel>
                        <div className="flex h-10 items-center rounded-btn border border-line-2 bg-bg-warm px-3 text-body font-bold text-primary">
                          {Number.isFinite(unit.factor)
                            ? `${fmtInt(unit.factor)} ${unitDrafts[0]?.unit || ''}`
                            : 'กรอกจำนวนให้ถูกต้อง'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card title="3. ต้นทุนและราคาขาย">
            <div className="mb-5 grid grid-cols-1 gap-4 rounded-card border border-primary/20 bg-primary-light/40 p-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <FieldLabel>ต้นทุนนี้เป็นราคาของหน่วย</FieldLabel>
                <select
                  aria-label="หน่วยของต้นทุน"
                  disabled={!canEdit}
                  value={costUnitId}
                  onChange={(event) => {
                    const nextUnitId = event.target.value;
                    const nextUnit = unitDrafts.find(
                      (unit) => unit.id === nextUnitId,
                    );
                    setCostUnitId(nextUnitId);
                    setCostInput(
                      moneyInputValue(
                        (form.cost ?? 0) * (nextUnit?.factor ?? 1),
                      ),
                    );
                  }}
                  className="h-10 rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10 disabled:bg-bg-warm"
                >
                  {unitDrafts.map((unit, index) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.unit || `หน่วย ${index + 1}`}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <FieldLabel>ต้นทุนตั้งต้น (บาท)</FieldLabel>
                <input
                  aria-label="ต้นทุนสินค้า"
                  disabled={!canEdit}
                  type="number"
                  min={0}
                  step="0.01"
                  value={costInput}
                  onChange={(event) => {
                    const value = event.target.value;
                    setCostInput(value);
                    set(
                      'cost',
                      baseCostFromSelectedUnit(
                        value ? parseBahtToSatang(value) : 0,
                        unitDrafts.find((unit) => unit.id === costUnitId)
                          ?.factor ?? 1,
                      ),
                    );
                  }}
                  className="num h-10 rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10 disabled:bg-bg-warm"
                  placeholder="เช่น 2160.00"
                />
                <span className="text-xs text-ink-3">
                  คิดเป็น {fmtAmount(form.cost ?? 0)} บาท /
                  {unitDrafts[0]?.unit || 'หน่วยเล็กสุด'}
                </span>
              </label>
            </div>

            <div className="space-y-3">
              {unitDrafts.map((unit, index) => {
                const health = unitHealth[index]!;
                const style = healthStyle[health.status];
                return (
                  <div
                    key={unit.id}
                    className="grid grid-cols-1 gap-3 rounded-card border border-line-2 p-4 sm:grid-cols-[1fr_150px]"
                  >
                    <div>
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span className="font-bold text-ink">
                          {unit.unit || `หน่วย ${index + 1}`}
                        </span>
                        <span
                          className={`rounded-chip px-2 py-0.5 text-xs font-bold ${style.className}`}
                        >
                          {style.label}
                        </span>
                      </div>
                      <div className="text-sm text-ink-3">
                        ต้นทุนคำนวณ {fmtAmount(health.cost)} บาท
                        {health.profit !== null && (
                          <>
                            {' · '}กำไร {fmtAmount(health.profit)} บาท
                            {' · '}Margin {health.marginPct!.toFixed(1)}%
                          </>
                        )}
                      </div>
                    </div>
                    <label className="flex flex-col gap-1.5">
                      <FieldLabel>ราคาขาย (บาท)</FieldLabel>
                      <input
                        aria-label={`ราคาแถว ${index + 1}`}
                        disabled={!canEdit}
                        type="number"
                        min={0}
                        step="0.01"
                        value={unit.priceInput}
                        onChange={(event) => {
                          const value = event.target.value;
                          setUnit(unit.id, {
                            priceInput: value,
                            price: value ? parseBahtToSatang(value) : 0,
                          });
                        }}
                        className="num h-10 rounded-btn border border-line bg-surface px-3 text-body focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10 disabled:bg-bg-warm"
                        required
                      />
                    </label>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card title="รูปภาพสินค้า">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-card border border-line-2 bg-bg-warm text-xs text-ink-3">
                {form.imageUrl ? (
                  <img
                    src={form.imageUrl}
                    alt="ตัวอย่างสินค้า"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  'ไม่มีรูป'
                )}
              </div>
              <div className="space-y-2">
                <label className="inline-flex cursor-pointer rounded-btn border border-line bg-surface px-4 py-2 text-sm font-bold text-ink">
                  เลือกรูปภาพ
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={!canEdit}
                    className="hidden"
                  />
                </label>
                {form.imageUrl && canEdit && (
                  <button
                    type="button"
                    onClick={() => set('imageUrl', '')}
                    className="ml-2 text-sm font-bold text-danger"
                  >
                    ลบรูป
                  </button>
                )}
                <div className="text-xs text-ink-3">
                  รูปภาพช่วยลดการเลือกสินค้าผิดใน Product Picker
                </div>
              </div>
            </div>
          </Card>

          {(submitError || mutation.error) && (
            <div
              role="alert"
              className="rounded-card border border-danger/20 bg-danger-light px-4 py-3 text-sm font-semibold text-danger"
            >
              {submitError ||
                (mutation.error instanceof Error
                  ? mutation.error.message
                  : 'บันทึกสินค้าไม่สำเร็จ')}
            </div>
          )}

          <div className="sticky bottom-0 z-10 flex gap-2 border-t border-line-2 bg-bg/95 py-3 backdrop-blur">
            <Btn
              kind="default"
              type="button"
              onClick={() => navigate('/products')}
            >
              ยกเลิก
            </Btn>
            {canEdit && (
              <Btn kind="primary" type="submit">
                บันทึก
              </Btn>
            )}
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6">
          <Card title="สรุปสินค้า" className="border border-line-2 shadow-md">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-ink-3">หน่วยเล็กสุด</span>
                <strong>{unitDrafts[0]?.unit || '—'}</strong>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-ink-3">หน่วยใหญ่สุด</span>
                <strong>{unitDrafts.at(-1)?.unit || '—'}</strong>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-ink-3">จำนวนเทียบหน่วยฐาน</span>
                <strong className="num">
                  {Number.isFinite(unitDrafts.at(-1)?.factor)
                    ? fmtInt(unitDrafts.at(-1)?.factor ?? 1)
                    : '—'}
                </strong>
              </div>
              <div className="flex justify-between gap-3 border-t border-line-2 pt-3">
                <span className="text-ink-3">ต้นทุนต่อหน่วยเล็กสุด</span>
                <strong className="num text-primary">
                  {fmtAmount(form.cost ?? 0)} บาท
                </strong>
              </div>
            </div>
          </Card>

          <Card title="สถานะราคา">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between rounded-btn bg-danger-light px-3 py-2 text-danger">
                <span>ขาดทุน</span>
                <strong>{lossCount} หน่วย</strong>
              </div>
              <div className="flex justify-between rounded-btn bg-warn-light px-3 py-2 text-warn">
                <span>กำไรต่ำกว่า 10%</span>
                <strong>{warningCount} หน่วย</strong>
              </div>
              <div className="flex justify-between rounded-btn bg-ok-light px-3 py-2 text-ok">
                <span>ราคาปกติ</span>
                <strong>
                  {
                    unitHealth.filter((item) => item.status === 'healthy')
                      .length
                  }{' '}
                  หน่วย
                </strong>
              </div>
            </div>
          </Card>
        </aside>
      </form>
    </div>
  );
}
